import { siteText } from '../siteText'
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { Check, Download, Lock, MoreHorizontal, RefreshCw, Send, Settings2 } from 'lucide-react'
import { ClipNotes, EditorialPanel, editorialOrder } from '../components/Editorial'
import { api, ApiRequestError } from '../api'
import { deliveryPollInterval } from '../deliveryPolling'
import { useViewActive } from '../viewActivity'
import { UndoToast } from '../components/Deletion'
import { useAsync } from '../hooks'
import { currentJob } from '../jobStatus'
import { ClipRail } from '../components/ClipRail'
import { ClipPreview } from '../components/ClipPreview'
import { ReturnExport } from '../components/ReturnExport'
import { BalancedClipTitle } from '../components/ClipTitle'
import { ApprovalAttribution } from '../components/ApprovalAttribution'
import { WorkspaceAction, WorkspaceActions } from '../components/WorkspaceHeader'
import './EditorQueueView.css'
import type { Clip, Delivery, User } from '../types'
import { Button, Disclosure, Modal, Notice } from '../components/ui'

export function EditorQueueView({ user, focusClipId, focusClipSequence = 0 }: { user: User; focusClipId?: string | null; focusClipSequence?: number }) {
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; text: string } | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('')
  const [editingBusy, setEditingBusy] = useState(false)
  const preview = useRef<HTMLVideoElement>(null)
  const [playRequest, setPlayRequest] = useState<{ clipId: string; sequence: number } | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [undo, setUndo] = useState<{ id: string; label: string } | null>(null)
  const [workspaceReady, setWorkspaceReady] = useState(false)
  const queue = useAsync(() => api.clips(), [], { pollMs: 3000 })
  const requestedFocus = useRef<string | null>(null)
  const focused = useAsync(async () => {
    if (!focusClipId) return null
    try {
      const clip = await api.clip(focusClipId)
      return { id: focusClipId, clip, unavailable: null }
    } catch (error) {
      if (error instanceof ApiRequestError && [403, 404].includes(error.status)) return { id: focusClipId, clip: null, unavailable: 'This clip is no longer available. It may have been removed or your access changed.' }
      throw error
    }
  }, [focusClipId, focusClipSequence], { enabled: Boolean(focusClipId), pollMs: 3000 })
  useEffect(() => {
    requestedFocus.current = focusClipId || null
    if (focusClipId) {
      setActiveId(focusClipId); setQuery(''); setStatus(''); setNotice(null)
      setPlayRequest(current => ({ clipId: focusClipId, sequence: (current?.sequence || 0) + 1 }))
    }
  }, [focusClipId, focusClipSequence])
  const canDelete = true
  const clips = useMemo(() => (queue.data || []).filter((clip) => ['approved', 'editing', 'changes_requested'].includes(clip.status)).sort(editorialOrder), [queue.data])
  const visibleClips = useMemo(() => clips.filter(clip => (!status || clip.status === status) && (!query.trim() || `${clip.title} ${clip.speaker} ${clip.rationale} ${clip.claimed_by?.name || clip.editorial_plan?.assigned_editor?.name || ''}`.toLowerCase().includes(query.trim().toLowerCase()))), [clips, query, status])
  const explicitFocus = Boolean(activeId && requestedFocus.current === activeId)
  const focusedClip = focused.data?.id === activeId ? focused.data.clip : null
  const focusError = explicitFocus ? focused.error || (focused.data?.id === activeId ? focused.data.unavailable : null) : null
  const focusMoved = explicitFocus && focusedClip && !['approved', 'editing', 'changes_requested'].includes(focusedClip.status)
  const activeClip = explicitFocus ? !focusError && !focusMoved ? focusedClip : null : visibleClips.find(clip => clip.id === activeId)
  useEffect(() => { if ((queue.data || queue.error) && (explicitFocus ? activeClip || focusError || focusMoved : !visibleClips.length || activeClip)) setWorkspaceReady(true) }, [queue.data, queue.error, visibleClips.length, activeClip, explicitFocus, focusError, focusMoved])
  useEffect(() => {
    if (activeId && requestedFocus.current === activeId) return
    if (queue.data && !editingBusy && !activeClip) setActiveId(visibleClips[0]?.id || null)
  }, [queue.data, activeClip, visibleClips, editingBusy])
  function selectClip(id: string) {
    if (editingBusy) return
    requestedFocus.current = null
    if (id === activeId && preview.current) {
      if (preview.current.paused) void preview.current.play().catch(() => undefined)
      else preview.current.pause()
      return
    }
    preview.current?.pause()
    setNotice(null)
    setPlayRequest(current => ({ clipId: id, sequence: (current?.sequence || 0) + 1 }))
    setActiveId(id)
  }
  async function update(action: () => Promise<unknown>, success: string) { setNotice(null); try { await action(); await queue.refresh(); setNotice({ tone: 'success', text: success }) } catch (err) { setNotice({ tone: 'error', text: err instanceof Error ? err.message : 'The editor action could not be completed.' }) } }
  async function removeClip(clip: Clip) {
    setDeletingId(clip.id); setNotice(null)
    try {
      await api.deleteClip(clip.id)
      queue.setData((current) => current?.filter((item) => item.id !== clip.id) ?? null)
      if (activeId === clip.id) setActiveId(null)
      setUndo({ id: clip.id, label: clip.title })
      void queue.refresh()
    } catch (err) { setNotice({ tone: 'error', text: err instanceof ApiRequestError ? err.message : 'The clip could not be removed.' }) }
    finally { setDeletingId(null) }
  }
  async function restoreClip() {
    if (!undo) return
    const pending = undo
    setDeletingId(pending.id)
    try {
      await api.restoreClip(pending.id)
      setUndo(null)
      await queue.refresh()
      setActiveId(pending.id)
      setNotice({ tone: 'success', text: `${pending.label} restored.` })
    } catch (err) { setNotice({ tone: 'error', text: err instanceof ApiRequestError ? err.message : 'The clip could not be restored.' }) }
    finally { setDeletingId(null) }
  }
  const emptyWorkspace = visibleClips.length === 0 && !activeClip
  if (!workspaceReady) return <div className="page-content review-page editor-page"><div className="review-initial-loading" role="status" aria-label="Loading editor queue"><span className="motion-logo workspace-loading-logo" aria-hidden="true" /></div></div>
  return <div className="page-content review-page editor-page">
    <WorkspaceActions><WorkspaceAction label="Refresh Queue" onClick={() => void queue.refresh()} disabled={queue.loading || editingBusy}><RefreshCw size={18} className={queue.loading ? 'spin' : ''} /></WorkspaceAction></WorkspaceActions>
    {notice ? <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>{siteText(notice.text)}</Notice> : null}
    {focusError ? <Notice>{siteText(focusError)}</Notice> : focusMoved ? <Notice>This clip has moved out of the editor queue. Open its notification again to see its current review.</Notice> : null}
    <div className={`review-workspace editor-review-workspace${emptyWorkspace ? ' is-empty' : ''}`}>
      <ClipRail emptyWorkspace={emptyWorkspace} filtersInHeader scope="editor" clips={activeClip && !visibleClips.some(clip => clip.id === activeClip.id) ? [activeClip, ...visibleClips] : visibleClips} jobs={[]} loading={queue.loading && !queue.data} error={queue.error} selectedId={activeId} onSelect={selectClip} query={query} onQuery={value => { if (!editingBusy) { requestedFocus.current = null; setQuery(value) } }} status={status} onStatus={value => { if (!editingBusy) { requestedFocus.current = null; setStatus(value) } }} canDelete={canDelete && !editingBusy} deletingId={deletingId} onDelete={clip => void removeClip(clip)} selectionDisabled={editingBusy} />
      {activeClip ? <EditorActionPanel key={activeClip.id} clip={activeClip} user={user} onSaved={queue.refresh} onUpdate={update} preview={preview} playRequest={playRequest} onBusyChange={setEditingBusy} /> : !emptyWorkspace ? <div className="review-inspector" /> : null}
    </div>
    {undo ? <UndoToast key={undo.id} message={`${undo.label} removed.`} onUndo={() => void restoreClip()} onDismiss={() => setUndo(null)} busy={deletingId === undo.id} /> : null}
  </div>
}

function EditorActionPanel({ clip, user, onUpdate, onSaved, preview, playRequest, onBusyChange }: { onSaved: () => Promise<unknown>; clip: Clip; user: User; onUpdate: (action: () => Promise<unknown>, success: string) => Promise<void>; preview: RefObject<HTMLVideoElement>; playRequest: { clipId: string; sequence: number } | null; onBusyChange: (busy: boolean) => void }) {
  const [rationaleOpen, setRationaleOpen] = useState(true)
  const [delivery, setDelivery] = useState<Delivery | null>(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState('')
  const [returning, setReturning] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const jobs = useAsync(() => api.clipJobs(clip.id), [clip.id], { pollMs: 3000 })
  const returnJob = currentJob((jobs.data || []).filter(job => job.clip_id === clip.id && job.kind === 'final_return' && job.version_id === (clip.approved_version_id || clip.latest_version_id)))
  const processingReturn = returnJob?.status === 'queued' || returnJob?.status === 'running'
  const packageJob = currentJob((jobs.data || []).filter(job => job.clip_id === clip.id && job.kind === 'premiere_request' && (!job.actor_id || job.actor_id === user.id) && job.version_id === (clip.approved_version_id || clip.latest_version_id)))
  const preparingPackage = packageJob?.status === 'queued' || packageJob?.status === 'running'
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [deliveryLoading, setDeliveryLoading] = useState(true)
  const viewActive = useViewActive()
  const more = useRef<HTMLDetailsElement>(null)
  const version = clip.versions?.find((item) => item.id === clip.latest_version_id) || clip.versions?.[0]
  const baseVersionId = clip.approved_version_id || clip.latest_version_id || version?.id || ''
  const previewVersion = clip.versions?.find(item => item.id === baseVersionId) || version
  const isOwner = clip.claimed_by?.id === user.id
  const canRelease = Boolean(clip.claimed_by)
  const canImport = true
  const pending = delivery?.status === 'pending' || delivery?.status === 'downloaded'
  const imported = delivery?.status === 'imported' || delivery?.status === 'returned'
  const busy = importing || returning || actionBusy
  const packageFailed = packageJob?.status === 'failed' || packageJob?.status === 'interrupted'
  const importWorking = importing || preparingPackage || pending
  const importFailure = importError || (!importWorking && !imported && delivery?.status === 'failed' ? delivery.message || 'The Premiere import was interrupted.' : '') || (!importWorking && !imported && packageFailed ? packageJob?.error || 'Package preparation was interrupted.' : '')
  const importPercent = preparingPackage && packageJob?.status === 'running' && packageJob.progress != null ? Math.floor(Math.max(0, Math.min(1, packageJob.progress)) * 100) : null
  const importLabel = importing ? 'Preparing for Premiere' : preparingPackage ? packageJob?.status === 'queued' ? 'Queued for Premiere' : 'Preparing Premiere package' : delivery?.status === 'downloaded' ? 'Importing into Premiere' : pending ? 'Waiting for Premiere' : importFailure ? 'Retry Premiere import' : imported ? 'Import to Premiere again' : 'Import to Premiere'
  const importDetail = importFailure || (preparingPackage ? packageJob?.message : pending ? delivery?.message : '') || ''
  useEffect(() => { onBusyChange(busy); return () => onBusyChange(false) }, [busy, onBusyChange])
  function closeMenu() { if (more.current) more.current.open = false }
  useEffect(() => {
    const dismiss = (event: PointerEvent) => { if (!more.current?.contains(event.target as Node)) closeMenu() }
    document.addEventListener('pointerdown', dismiss)
    return () => document.removeEventListener('pointerdown', dismiss)
  }, [])

  useEffect(() => {
    setDelivery(null)
    if (!canImport) { setDeliveryLoading(false); return }
    setDeliveryLoading(true)
    let active = true
    void api.deliveries().then((items) => {
      if (active) setDelivery(items.find((item) => item.clip_id === clip.id && item.version_id === baseVersionId) || null)
    }).catch(() => { /* The import action can still retry if status is unavailable. */ }).finally(() => { if (active) setDeliveryLoading(false) })
    return () => { active = false }
  }, [clip.id, baseVersionId, canImport, packageJob?.status])

  useEffect(() => {
    // Only a visible Editor view polls; a failed delivery is checked slowly
    // because the CLI connector can still retry and import it.
    const interval = viewActive ? deliveryPollInterval(delivery, imported) : null
    const deliveryId = delivery?.id
    if (!interval || !deliveryId) return
    let active = true
    const timer = window.setInterval(() => {
      void api.delivery(deliveryId).then((updated) => { if (active) setDelivery(updated) }).catch(() => {})
    }, interval)
    return () => { active = false; window.clearInterval(timer) }
  }, [viewActive, delivery?.id, delivery?.status, imported])

  async function importToPremiere(refresh = false) {
    if (importing || !baseVersionId) return
    setImporting(true); setImportError('')
    try {
      const result = await api.premiere(clip.id, baseVersionId, refresh)
      if ('kind' in result) jobs.setData(items => [result, ...(items || []).filter(item => item.id !== result.id)])
      else setDelivery(result)
      await jobs.refresh()
      await onSaved()
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'The Premiere import could not be started.')
    } finally { setImporting(false) }
  }

  async function updateEdit(action: () => Promise<unknown>, success: string) {
    if (busy) return
    closeMenu()
    setActionBusy(true)
    try { await onUpdate(action, success) }
    finally { setActionBusy(false) }
  }

  return <section className="review-inspector" aria-label="Editing workspace">
    <div className="review-media-column"><div className="review-media-stack"><ClipPreview videoRef={preview} clip={clip} jobs={jobs.data || []} versionId={previewVersion?.id || null} loading={false} playRequest={playRequest} /></div></div>
    <div className="review-detail-column editor-detail-column" role="region" aria-label="Editing actions and team notes" tabIndex={0}>
      <div className="detail-content">
        <header className="review-clip-header editor-clip-header">
          <div className="editor-title-row"><BalancedClipTitle title={siteText(clip.title)} />
      <details ref={more} className="editor-more" onKeyDown={event => { if (event.key === 'Escape') { closeMenu(); more.current?.querySelector('summary')?.focus() } }}>
        <summary aria-label="More clip actions"><MoreHorizontal size={20} /></summary>
        <div className="editor-more-menu">
          {true ? <button type="button" disabled={busy} onClick={() => { closeMenu(); setSettingsOpen(true) }}><Settings2 size={15} /> Priority &amp; Assignment</button> : null}
          {delivery?.package_url ? <a href={delivery.package_url} target="_blank" rel="noreferrer" onClick={closeMenu}><Download size={15} /> Download Package</a> : null}
          {isOwner && imported ? <button type="button" disabled={busy || preparingPackage || processingReturn} onClick={() => { closeMenu(); void importToPremiere(true) }}><RefreshCw size={15} /> Refresh Premiere Package</button> : null}
          {isOwner ? <ReturnExport compact disabled={busy || preparingPackage || processingReturn} clip={clip} baseVersionId={baseVersionId} onUpdate={onUpdate} onBusy={setReturning} /> : null}
          {isOwner && (returnJob?.status === 'failed' || returnJob?.status === 'interrupted') ? <button type="button" disabled={busy || preparingPackage || processingReturn} onClick={() => void updateEdit(() => api.retryJob(returnJob.id), 'Retry queued.')}><RefreshCw size={15} /> Retry Finished Edit Processing</button> : null}
          {canRelease ? <button type="button" disabled={busy || preparingPackage || processingReturn} onClick={() => void updateEdit(() => api.release(clip.id), 'Clip released back to the queue.')}><Lock size={15} /> Release Edit</button> : null}
          {!isOwner && !delivery ? <span>{siteText(clip.claimed_by ? `Editing with ${clip.claimed_by.name}` : 'No additional actions')}</span> : null}
        </div>
      </details>
          </div>
        </header>
        <ApprovalAttribution clip={clip} version={previewVersion} />
        {clip.rationale ? <div className="detail-source-section editor-description"><Disclosure title="Why This Clip" open={rationaleOpen} onToggle={() => setRationaleOpen(open => !open)}><p className="editor-rationale">{siteText(clip.rationale)}</p></Disclosure></div> : null}
        <div className="editor-import">
          <button type="button" className={`button button-primary editor-import-button${importWorking ? ' is-working' : ''}${imported && !importWorking ? ' is-complete' : ''}`} onClick={() => void importToPremiere(imported)} disabled={!canImport || !baseVersionId || busy || deliveryLoading || preparingPackage || pending || processingReturn} aria-busy={importWorking} aria-label={importLabel} title={importDetail || importLabel}>
            {importWorking ? <span className="premiere-motion" aria-hidden="true"><i /><i /><i /></span> : imported ? <Check size={16} aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
            <span className="premiere-action-copy" aria-live="polite"><strong>{siteText(importLabel)}</strong></span>
            {importPercent != null ? <span className="premiere-action-percent">{importPercent}%</span> : null}
            {importWorking ? <span className={`premiere-action-track${importPercent == null ? ' is-indeterminate' : ''}`} aria-hidden="true"><i style={importPercent == null ? undefined : { width: `${importPercent}%` }} /></span> : null}
          </button>
          {importFailure ? <p className="prepared-project-error" role="alert">{siteText(importFailure)}</p> : null}
        </div>
        <ClipNotes key={clip.id} clip={clip} versionId={previewVersion?.id} user={user} />
      </div>
    </div>
    {settingsOpen ? <Modal title="Priority & Assignment" className="editor-settings-dialog" onClose={() => setSettingsOpen(false)}><EditorialPanel clip={clip} user={user} onSaved={onSaved} /></Modal> : null}
  </section>
}
